import { IArticle, IQuantityPerUnit, ISingleOrderedArticleLine } from '@alberta/konexi-shared';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { returnDurationText } from 'src/app/business/general/duration2text-factory';
import { articleIsForUse } from 'src/app/business/order/article-is-for-use-helper';

import { DirectOrderArticleLineViewModel } from '../../../business/direct-order/direct-order-articleline-view-model';
import { SingleOrderedArticleLineViewModel } from '../../../business/single-order/single-ordered-articleline-view-model';
import { ArticleUnitPipe } from '../../../common/pipes/article/articleunit-pipe';
import { SelectionItem } from '../../models/selectionItem';
import { DayToYearDurations } from '../add-article/add-article.component';
import { TenantConfigService } from './../../services/tenant-config/tenant-config.service';
import { SelectionService } from './../services/selection.service';

@Component({
  selector: 'itl-order-article-edit-modal',
  templateUrl: './order-article-edit-modal.component.html',
  styleUrls: ['./order-article-edit-modal.component.scss'],
})
export class OrderArticleEditComponent implements OnInit {
  @Input() public articleLine: SingleOrderedArticleLineViewModel | DirectOrderArticleLineViewModel;
  @Input() public showBatchNumber = true;
  @Input() public showSerialNumber = true;
  @Input() public showDeleteArticleButton;

  public form: UntypedFormGroup;
  public article: IArticle;

  public period: AbstractControl;
  public quantity: AbstractControl;
  public interval: AbstractControl;
  public packagingId: AbstractControl;
  public batchNumber: AbstractControl;
  public serialNumber: AbstractControl;
  public storageLocation: AbstractControl;

  public units: SelectionItem[] = [];
  public periods: SelectionItem[] = [];
  public durations = DayToYearDurations;
  public storageLocations: SelectionItem[] = [];

  public isDirectOrder: boolean;
  public showResmedTenant: boolean;

  get showInterval(): boolean {
    return !articleIsForUse(this.article);
  }

  constructor(
    private _modalController: ModalController,
    private _formBuilder: UntypedFormBuilder,
    private _translate: TranslateService,
    private _articleUnitPipe: ArticleUnitPipe,
    private _selectionService: SelectionService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _tenantConfigService: TenantConfigService
  ) {
    const durationSelectionItems = DayToYearDurations.map(
      timePeriod => new SelectionItem(timePeriod, returnDurationText(this._translate, timePeriod, true))
    );
    this.periods.push(...durationSelectionItems);
  }

  public async ngOnInit(): Promise<void> {
    this.checkForDirectOrder(this.articleLine);
    this.showResmedTenant = await this._tenantConfigService.showResmedTenant();
    this.article = this.articleLine.article;
    this.initializeFormBuilder();
    await this.getStorageLocations();
    this.form.controls['packagingId'].markAsTouched();

    const packingUnitsSelectionItems = this.getAvailablePackagingUnits().map(packagingUnit =>
      this.createPackagingUnitItem(packagingUnit)
    );
    this.units.push(...packingUnitsSelectionItems);
    if (this.units.length === 1) {
      this.articleLine.packagingUnitId = this.units[0].id;
      this.packagingId.setValue(this.units[0].id);
    } else if (this.units.length > 1) {
      const defaultPackagingUnit = this.article.packagingUnits.find((f: any) => f.defaultPackagingUnit);
      if (defaultPackagingUnit) {
        this.articleLine.packagingUnitId = defaultPackagingUnit._id;
        this.packagingId.setValue(defaultPackagingUnit._id);
      }
    }
  }

  private checkForDirectOrder(articleLine) {
    if (articleLine instanceof DirectOrderArticleLineViewModel) {
      this.isDirectOrder = true;
    } else {
      this.isDirectOrder = false;
    }
  }

  private initializeFormBuilder() {
    this.form = this._formBuilder.group({
      _id: [this.articleLine._id],
      quantity: [this.articleLine.quantity],
      packagingId: [this.articleLine.packagingUnitId],
      batchNumber: [(this.articleLine as ISingleOrderedArticleLine).batchNumber],
      serialNumber: [(this.articleLine as ISingleOrderedArticleLine).serialNumber],
      storageLocation: [(this.articleLine as ISingleOrderedArticleLine).storageLocation],
    });
    this.period = this.form.controls['period'];
    this.quantity = this.form.controls['quantity'];
    this.interval = this.form.controls['interval'];
    this.packagingId = this.form.controls['packagingId'];
    this.batchNumber = this.form.controls['batchNumber'];
    this.serialNumber = this.form.controls['serialNumber'];
    this.storageLocation = this.form.controls['storageLocation'];
  }

  private createPackagingUnitItem(packagingUnit: IQuantityPerUnit): SelectionItem {
    const stueck = this._translate.instant('article.edit.units.stk');
    return new SelectionItem(
      packagingUnit._id,
      `${this._articleUnitPipe.transform(packagingUnit.unit)} (${packagingUnit.quantity} ${stueck})`
    );
  }

  private getAvailablePackagingUnits() {
    return this.article.packagingUnits.filter(packagingUnit => !packagingUnit.archived);
  }

  private async getStorageLocations() {
    const locations = await this._tenantConfigService.getStorageLocations();
    if (locations && locations.length) {
      locations.forEach(location => {
        this.storageLocations.push(new SelectionItem(location.code, location.name));
      });
    }
  }

  public async openUnitSelect() {
    const result = await this._selectionService.openSelection(
      this.articleLine.packagingUnit._id,
      this.units,
      this._translate.instant('order.edit.unitSelect')
    );
    if (!result.canceled) {
      this.articleLine.packagingUnitId = result.value;
      this.articleLine.packagingUnit = this.articleLine.article.packagingUnits.find(
        packagingUnit => packagingUnit._id === result.value
      );
      this.articleLine.unit = this.articleLine.packagingUnit.unit;
      this._changeDetectorRef.markForCheck();
    }
  }
  public addOne() {
    this.articleLine.quantity++;
    if (this.articleLine instanceof SingleOrderedArticleLineViewModel && this.articleLine.maxAmount) {
      if (this.articleLine.active) {
        this.articleLine.maxAmount.currentQuantity += +this.articleLine.packagingUnit.quantity;
      }
    }
  }

  public deleteOne() {
    if (this.articleLine.quantity > 1) {
      this.articleLine.quantity--;
      if (this.articleLine instanceof SingleOrderedArticleLineViewModel && this.articleLine.maxAmount) {
        if (this.articleLine.active) {
          this.articleLine.maxAmount.currentQuantity -= +this.articleLine.packagingUnit.quantity;
        }
      }
    }
  }

  public togglePrivateSaleCheckbox($event, articleLine) {
    articleLine.isPrivateSale = $event.detail.checked;
  }

  public async save() {
    await this._modalController.dismiss(this.articleLine).catch(error => console.log(error));
  }

  public async cancel() {
    await this._modalController.dismiss({ isModalCanceled: true }).catch(error => console.log(error));
  }

  public async removeArticleFromShoppingCart() {
    await this._modalController.dismiss({ flaggedForDeletion: true }).catch(error => console.log(error));
  }
}
